import React from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MapPinIcon, ScaleIcon, ShieldCheckIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { FIRM } from '../../lib/constants';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { Link } from 'react-router-dom';
const trustPoints = ['Perth / Nedlands based', 'Business, property, family, succession & dispute lawyers', 'Practical advice before problems become expensive'];
const quickPaths = [{
  label: 'Business & commercial matter',
  slug: 'commercial-business-law'
}, {
  label: 'A dispute or claim',
  slug: 'dispute-resolution'
}, {
  label: 'Buying or selling property',
  slug: 'property-law'
}, {
  label: 'Family or separation',
  slug: 'family-law'
}, {
  label: 'Wills, estates & succession',
  slug: 'wills-estates'
}];
export function Hero() {
  return <section className="relative overflow-hidden bg-navy text-cream" data-id="element-133">
      {/* Perth / Swan River foreshore & marina — echoing the firm's Nedlands (Matilda Bay) imagery */}
      <div className="absolute inset-0 opacity-25" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1524293581917-878a6d017c71?auto=format&fit=crop&w=1600&q=80)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }} aria-hidden data-id="element-134" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70" aria-hidden data-id="element-135" />

      <Container className="relative grid grid-cols-1 items-center gap-12 py-16 lg:grid-cols-12 lg:gap-10 lg:py-24" data-id="element-136">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="lg:col-span-7" data-id="element-137">
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-cream/15 bg-cream/5 px-3.5 py-1.5 text-xs font-medium tracking-wide text-cream/80" data-id="element-138">
            <MapPinIcon className="h-3.5 w-3.5 text-brass-light" data-id="element-139" />
            Nedlands · Perth · Western Australia
          </p>
          <h1 className="font-serif text-4xl font-500 leading-[1.08] tracking-tight sm:text-5xl lg:text-[3.4rem]" data-id="element-140">
            Clear legal advice when the decision matters.
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-cream/75" data-id="element-141">
            Cullen Macleod helps WA businesses, families and property clients
            resolve legal issues with practical advice, commercial thinking and
            a clear path forward.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-142">
            <Button href={FIRM.phoneHref} variant="primary" size="lg" data-id="element-143">
              <PhoneIcon className="h-5 w-5" data-id="element-144" />
              Speak with a lawyer
            </Button>
            <Button to="/enquiry" variant="outline" size="lg" className="border-cream/25 text-cream hover:border-cream hover:bg-cream/10" data-id="element-145">
              Send a confidential enquiry
              <ArrowRightIcon className="h-4 w-4" data-id="element-146" />
            </Button>
          </div>

          <ul className="mt-8 grid gap-2.5 sm:grid-cols-2" data-id="element-147">
            {trustPoints.map(t => <li key={t} className="flex items-start gap-2.5 text-sm text-cream/80" data-id="element-148">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brass-light" data-id="element-149" />
                {t}
              </li>)}
            <li className="flex items-start gap-2.5 text-sm" data-id="element-150">
              <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-brass-light" data-id="element-151" />
              <a href={FIRM.phoneHref} className="font-semibold text-cream hover:text-brass-light" data-id="element-152">
                Call {FIRM.phoneDisplay}
              </a>
            </li>
          </ul>
        </motion.div>

        {/* right selector card */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.12
      }} className="lg:col-span-5" data-id="element-153">
          <div className="rounded-2xl border border-cream/10 bg-cream p-6 text-navy shadow-mega sm:p-7" data-id="element-154">
            <div className="mb-1 flex items-center gap-2" data-id="element-155">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy/5" data-id="element-156">
                <ScaleIcon className="h-5 w-5 text-brass-dark" data-id="element-157" />
              </span>
              <h2 className="font-serif text-xl text-navy" data-id="element-158">How can we help?</h2>
            </div>
            <p className="mb-4 text-sm text-ink/60" data-id="element-159">
              Choose a starting point and we'll point you to the right team.
            </p>
            <ul className="space-y-2" data-id="element-160">
              {quickPaths.map(p => <li key={p.slug} data-id="element-161">
                  <Link to={`/services/${p.slug}`} className="group flex items-center justify-between rounded-lg border border-navy/10 px-4 py-3 text-sm font-medium text-navy transition-all hover:border-brass hover:bg-brass/[0.04]" data-id="element-162">
                    {p.label}
                    <ArrowRightIcon className="h-4 w-4 text-navy/30 transition-all group-hover:translate-x-0.5 group-hover:text-brass-dark" data-id="element-163" />
                  </Link>
                </li>)}
            </ul>
            <Link to="/enquiry" className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-navy px-4 py-3 text-sm font-semibold text-cream transition-colors hover:bg-navy-700" data-id="element-164">
              <ShieldCheckIcon className="h-4 w-4 text-brass-light" data-id="element-165" />
              Not sure? Start a confidential enquiry
            </Link>
          </div>
        </motion.div>
      </Container>
    </section>;
}